// Hazard analysis for the Caddie.
//
// Given the player's current position, the green's position, and a list of
// hazards/zones on the hole, produces:
//   • per-hazard distance + side + "on-line" flag
//   • a single short strategy line ("bunker 165y right · carry 175y or lay up short")
//
// Distance model is intentionally simple — we work in yards, not meters, to
// stay aligned with the Caddie suggestion engine.
//
// Coordinate math (haversine + bearing) is duplicated from
// <DistanceToGreen /> to keep this module standalone/testable.

const M_TO_Y = 1.09361;

function toRad(d) { return (d * Math.PI) / 180; }

export function distanceYards(lat1, lng1, lat2, lng2) {
  const R = 6371000; // meters
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a)) * M_TO_Y;
}

export function bearingDeg(lat1, lng1, lat2, lng2) {
  const φ1 = toRad(lat1), φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Smallest signed delta between two compass bearings. */
function angleDelta(a, b) {
  let d = ((b - a + 540) % 360) - 180;
  return d; // (-180, 180]  negative = left of A, positive = right of A
}

/**
 * For an elongated/edge-pinned hazard, project both edge points onto the shot
 * line player→green and pick the NEAR edge (smallest along-shot distance from
 * the player) and FAR edge (largest). This gives accurate carry / safe-past
 * distances regardless of which point the admin marked as "front".
 *
 *   player, green : { lat, lng }
 *   front, back   : { lat, lng } pinned by admin
 *
 * Returns { near_y, far_y } in yards along the shot line.
 */
function edgeProjection(player, green, front, back) {
  const shotBearing = bearingDeg(player.lat, player.lng, green.lat, green.lng);
  const project = (pt) => {
    const d = distanceYards(player.lat, player.lng, pt.lat, pt.lng);
    const b = bearingDeg(player.lat, player.lng, pt.lat, pt.lng);
    const ang = ((b - shotBearing + 540) % 360) - 180;
    return d * Math.cos((ang * Math.PI) / 180);   // along-shot component
  };
  const a = project(front);
  const b = project(back);
  return { near_y: Math.min(a, b), far_y: Math.max(a, b) };
}

const PRETTY_TYPE = {
  water: 'water',
  bunker: 'bunker',
  ob: 'OB',
  trees: 'trees',
  dogleg: 'dogleg',
  layup: 'layup',
};

/**
 * Analyse every hazard relative to player→green shot line.
 *
 *   player      { lat, lng }
 *   green       { lat, lng }
 *   hazards     [{ hazard_id, type, lat, lng, radius_y, label }]
 *   options     { club_yards: number? }  // chosen club's effective distance —
 *                 used to filter "relevant" hazards (in landing zone or
 *                 unreachable carry) so the strategy line ignores hazards the
 *                 player will easily fly over.
 *
 * Returns { analysed: [], strategy: '' }
 */
export function analyseHazards(player, green, hazards, options = {}) {
  if (!player || !green || !Array.isArray(hazards) || hazards.length === 0) {
    return { analysed: [], strategy: '' };
  }
  const targetDist = distanceYards(player.lat, player.lng, green.lat, green.lng);
  const shotBearing = bearingDeg(player.lat, player.lng, green.lat, green.lng);
  const clubY = Number(options.club_yards) || null;

  const analysed = hazards.map(h => {
    const dist = distanceYards(player.lat, player.lng, h.lat, h.lng);
    const hBearing = bearingDeg(player.lat, player.lng, h.lat, h.lng);
    const delta = angleDelta(shotBearing, hBearing); // +right, -left
    const absAngle = Math.abs(delta);
    const radius = Number(h.radius_y) || 10;

    // If the admin pinned both `front` and `back` edges, use edge projection
    // for accurate carry / safe-past. Otherwise fall back to centre±radius.
    let carryOver, layupShort, safePast;
    const hasEdges = h.front_lat != null && h.front_lng != null
                  && h.back_lat  != null && h.back_lng  != null;
    if (hasEdges) {
      const { near_y, far_y } = edgeProjection(
        player, green,
        { lat: h.front_lat, lng: h.front_lng },
        { lat: h.back_lat,  lng: h.back_lng  }
      );
      carryOver  = Math.max(0, Math.round(Math.max(near_y, far_y) + 5));
      layupShort = Math.max(0, Math.round(Math.min(near_y, far_y) - 10));
      safePast   = Math.max(0, Math.round(Math.max(near_y, far_y) + 5));
    } else {
      carryOver  = Math.max(0, Math.round(dist + radius + 5));
      layupShort = Math.max(0, Math.round(dist - radius - 10));
      safePast   = Math.max(0, Math.round(dist + radius + 5));
    }
    const side = absAngle < 5 ? 'center' : delta > 0 ? 'right' : 'left';

    // "In play" → within ~15° of shot line, between 20y and (target - 10y)
    const onLine = absAngle <= 18;
    const inCarryZone = dist > 15 && dist < targetDist - 5 && onLine;
    const beyondGreen = dist > targetDist + 15;

    // Per-hazard-type relevance. The previous "landing zone only" rule worked
    // for fairway bunkers (you fly over them with driver) but hid trees that
    // block flight, dogleg/layup aim points, and OB lines. Each type has a
    // different shape of danger — code accordingly.
    let relevant = true;
    if (clubY) {
      const inLandingZone = Math.abs(dist - clubY) <= 25;
      const sideNearGreen = !beyondGreen && Math.abs(dist - targetDist) < 30 && side !== 'center';
      const onLineWithinReach = onLine && !beyondGreen && dist <= clubY + 30;
      switch (h.type) {
        // Aim points — always show when ahead and on line.
        case 'dogleg':
        case 'layup':
          relevant = onLine && !beyondGreen;
          break;
        // Flight obstructions — block the BALL, not the landing. Show whenever
        // they're between you and the green near the line of play.
        case 'trees':
        case 'water':
        case 'ob':
          relevant = (inCarryZone) || sideNearGreen || (onLineWithinReach && dist > 30);
          break;
        // Fairway/greenside bunkers — fly over them with bigger clubs, only
        // mention if in actual landing zone, can't clear, or near the green.
        case 'bunker':
        default: {
          const cantClear = inCarryZone && carryOver > clubY - 5;
          relevant = inLandingZone || cantClear || sideNearGreen;
          break;
        }
      }
    }

    // Auto-generate a concise label when admin didn't set one. "near bunker",
    // "front-left bunker", "tree at 145y" etc. — so messages don't all look
    // identical when every hazard is unlabeled.
    const autoLabel = h.label || (() => {
      const sideTxt = side === 'center' ? '' : `${side} `;
      const niceType = PRETTY_TYPE[h.type] || h.type;
      return `${sideTxt}${niceType}`.trim();
    })();

    return {
      ...h,
      distance_y: Math.round(dist),
      bearing_deg: Math.round(hBearing),
      angle_off: Math.round(delta),
      side,
      on_line: onLine,
      in_carry: inCarryZone,
      beyond_green: beyondGreen,
      carry_over_y: carryOver,
      layup_short_y: layupShort,
      safe_past_y: safePast,
      has_edges: hasEdges,
      relevant,
      display_label: autoLabel,
    };
  });

  const strategy = buildStrategy(analysed, targetDist, clubY);
  return { analysed, strategy };
}

/**
 * Build ONE short strategy line. Priority:
 *   1. Any RELEVANT in-carry hazard (water/OB/bunker/trees) → tell carry or layup
 *   2. Dogleg on line → aim to dogleg
 *   3. Layup zone → suggested layup distance
 *   4. Side hazards near the green → "avoid X bunker right"
 *   5. Nothing relevant → "center of green"
 *
 * `clubY` filters carry threats to ones the chosen club can actually reach;
 * irrelevant hazards (e.g. a fairway bunker the player will fly over with
 * driver) are skipped so the line stays useful.
 */
/**
 * Build ONE short strategy line. Priority (most-impactful first):
 *   1. Water / OB carry (kills your shot if missed) — always wins
 *   2. Tree on flight path (blocks the ball mid-air)
 *   3. Dogleg / layup aim points (course management)
 *   4. Bunker carry / layup in landing zone
 *   5. Side hazards near the green
 *   6. Default — "center of green"
 *
 * `clubY` is used by analyseHazards to mark each entry as `relevant`, so the
 * filter below already respects landing-zone reasoning per type.
 */
function buildStrategy(analysed, targetDist, clubY) {
  const lbl = (h) => h.display_label || h.label || PRETTY_TYPE[h.type] || h.type;

  // 1. Water / OB carry threats (highest priority — these kill shots)
  const water = analysed
    .filter(h => h.relevant !== false && h.in_carry && ['water', 'ob'].includes(h.type))
    .sort((a, b) => a.distance_y - b.distance_y);
  if (water.length) {
    const h = water[0];
    return `${lbl(h)} at ${h.distance_y}y ${h.side} · carry ${h.carry_over_y}y to clear`;
  }

  // 2. Trees in flight path — the ball can't fly THROUGH them
  const trees = analysed
    .filter(h => h.relevant !== false && h.in_carry && h.type === 'trees')
    .sort((a, b) => a.distance_y - b.distance_y);
  if (trees.length) {
    const h = trees[0];
    return `${lbl(h)} ${h.distance_y}y ${h.side} · carry ${h.carry_over_y}y or aim ${h.side === 'left' ? 'right' : 'left'}`;
  }

  // 3. Aim points — dogleg / layup. These reshape the strategy.
  const dogleg = analysed.find(h => h.relevant !== false && h.type === 'dogleg' && h.on_line && !h.beyond_green);
  if (dogleg) {
    const leftover = Math.max(0, Math.round(targetDist - dogleg.distance_y));
    return `dogleg ${dogleg.distance_y}y · leaves ~${leftover}y to green`;
  }
  const layup = analysed.find(h => h.relevant !== false && h.type === 'layup' && h.on_line && !h.beyond_green);
  if (layup) {
    const leftover = Math.max(0, Math.round(targetDist - layup.distance_y));
    return `layup zone ${layup.distance_y}y · leaves ~${leftover}y in`;
  }

  // 4. Bunker carry / layup in landing zone
  const bunkerCarry = analysed
    .filter(h => h.relevant !== false && h.in_carry && h.type === 'bunker')
    .sort((a, b) => a.distance_y - b.distance_y);
  if (bunkerCarry.length) {
    const h = bunkerCarry[0];
    return `${lbl(h)} ${h.distance_y}y ${h.side} · carry ${h.carry_over_y}y or lay up to ${h.layup_short_y}y`;
  }

  // 5. Side hazards near the green (greenside bunkers / water close to pin)
  const nearGreen = analysed
    .filter(h => h.relevant !== false
      && ['bunker', 'water'].includes(h.type)
      && Math.abs(h.distance_y - targetDist) < 30
      && h.side !== 'center')
    .sort((a, b) => Math.abs(a.distance_y - targetDist) - Math.abs(b.distance_y - targetDist));
  if (nearGreen.length) {
    const h = nearGreen[0];
    return `aim center · avoid ${lbl(h)} ${h.side}`;
  }

  // 6. Nothing in play — clear shot
  return 'center of green · no hazards in play';
}

export const HAZARD_ICON = {
  water: '💧',
  bunker: '🏖️',
  ob: '🚧',
  trees: '🌳',
  dogleg: '↩️',
  layup: '🎯',
};

export const HAZARD_LABEL = {
  water: 'Water',
  bunker: 'Bunker',
  ob: 'OB',
  trees: 'Trees',
  dogleg: 'Dogleg',
  layup: 'Layup zone',
};
